import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';

function Dashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [balance, setBalance] = useState(null);
  const [recentTransfers, setRecentTransfers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Test Call
  const [testPhone, setTestPhone] = useState('');
  const [testCampaignId, setTestCampaignId] = useState('');
  const [testStatus, setTestStatus] = useState('idle'); // idle | calling | connected | failed
  const [testResult, setTestResult] = useState(null);
  const [testError, setTestError] = useState('');
  const timerRef = useRef(null);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (testStatus === 'calling') {
      timerRef.current = setTimeout(() => { setTestStatus('idle'); setTestResult(null); }, 60000);
    }
    return () => clearTimeout(timerRef.current);
  }, [testStatus]);

  const loadData = async () => {
    try {
      const [statsData, campaignsData, balanceData, transfersData] = await Promise.all([
        api.getDashboardStats(),
        api.getCampaigns(),
        api.getBalance(),
        api.getRecentTransfers()
      ]);
      setStats(statsData);
      setCampaigns(campaignsData.slice(0, 5));
      setBalance(balanceData);
      setRecentTransfers(transfersData || []);
      if (!testCampaignId && campaignsData.length > 0) setTestCampaignId(campaignsData[0].id);
    } catch (err) {
      console.error('Dashboard load error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTestCall = async () => {
    if (!testPhone.trim()) { setTestError('Enter a phone number'); return; }
    if (!testCampaignId) { setTestError('Select a campaign'); return; }
    setTestError(''); setTestStatus('calling'); setTestResult(null);
    try {
      const result = await api.testCall(testPhone.trim(), testCampaignId);
      setTestResult(result);
      setTestStatus('connected');
    } catch (err) {
      setTestError(err.message || 'Call failed');
      setTestStatus('failed');
    }
  };

  const resetTest = () => {
    clearTimeout(timerRef.current);
    setTestStatus('idle'); setTestResult(null); setTestError('');
  };

  const statusColor = { idle: '#6b7280', calling: '#f59e0b', connected: '#22c55e', failed: '#ef4444' };
  const statusLabel = { idle: '', calling: '📞 Dialing...', connected: '✅ Call Placed', failed: '❌ Failed' };

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div>
      <h1 style={{ fontSize: '2rem', fontWeight: 600, marginBottom: '1.5rem' }}>Dashboard</h1>

      {/* Stats */}
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-total-calls">
          <div className="stat-label">Total Calls Today</div>
          <div className="stat-value">{stats?.total_calls_today || 0}</div>
        </div>
        <div className="stat-card" data-testid="stat-active-calls">
          <div className="stat-label">Active Campaigns</div>
          <div className="stat-value">{stats?.active_calls || 0}</div>
        </div>
        <div className="stat-card" data-testid="stat-transferred">
          <div className="stat-label">Press 1 Transfers</div>
          <div className="stat-value">{stats?.transferred || 0}</div>
        </div>
        <div className="stat-card" data-testid="stat-balance"
          style={{ borderLeft: balance?.balance < 0 ? '4px solid #ef4444' : '4px solid #22c55e' }}>
          <div className="stat-label">Account Balance</div>
          <div className="stat-value" style={{ color: balance?.balance < 0 ? '#ef4444' : '#22c55e', fontSize: '1.5rem' }}>
            ${balance?.balance?.toFixed(2) || '0.00'}
          </div>
          <div style={{ fontSize: '0.75rem', color: 'hsl(var(--muted-foreground))', marginTop: '0.25rem' }}>
            {balance?.company_name || ''}
          </div>
        </div>
      </div>

      {/* Dispositions */}
      <div className="card">
        <div className="card-header"><h3 className="card-title">Disposition Breakdown</h3></div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <div><div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Live / AM</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: 'hsl(var(--baby-blue))' }}>{stats?.live_or_am || 0}</div></div>
          <div><div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>No Answer</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stats?.no_answer || 0}</div></div>
          <div><div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Busy</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600 }}>{stats?.busy || 0}</div></div>
          <div><div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Voicemail</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#8b5cf6' }}>{stats?.voicemail || 0}</div></div>
          <div><div style={{ fontSize: '0.875rem', color: 'hsl(var(--muted-foreground))' }}>Transferred</div>
            <div style={{ fontSize: '1.5rem', fontWeight: 600, color: '#22c55e' }}>{stats?.transferred || 0}</div></div>
        </div>
      </div>

      {/* TEST CALL PANEL */}
      <div className="card" style={{ marginTop: '1.5rem', borderLeft: '4px solid hsl(var(--baby-blue))' }}>
        <div className="card-header">
          <h3 className="card-title">📞 Test Call</h3>
          <p className="card-description">Dial a number manually using a real campaign — verifies service, audio and caller ID</p>
        </div>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>

          {/* Phone number */}
          <div style={{ flex: '1', minWidth: '180px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.4rem', color: 'hsl(var(--muted-foreground))' }}>
              Phone Number
            </label>
            <input data-testid="test-call-phone" type="tel"
              value={testPhone}
              onChange={e => setTestPhone(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && testStatus === 'idle' && handleTestCall()}
              placeholder="e.g. 6135551234"
              disabled={testStatus === 'calling'}
              style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px',
                border: '1px solid hsl(var(--border))', background: 'hsl(var(--input))',
                color: 'hsl(var(--foreground))', fontSize: '0.95rem' }} />
          </div>

          {/* Campaign */}
          <div style={{ flex: '2', minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '0.4rem', color: 'hsl(var(--muted-foreground))' }}>
              Campaign (audio + caller ID + transfer)
            </label>
            <select data-testid="test-call-campaign"
              value={testCampaignId}
              onChange={e => setTestCampaignId(e.target.value)}
              disabled={testStatus === 'calling'}
              style={{ width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px',
                border: '1px solid hsl(var(--border))', background: 'hsl(var(--input))',
                color: 'hsl(var(--foreground))', fontSize: '0.95rem' }}>
              <option value="">-- Select campaign --</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name}{c.audio_filename ? ` · ${c.audio_filename}` : ''}{c.caller_id ? ` · ID: ${c.caller_id}` : ''}
                </option>
              ))}
            </select>
          </div>

          {/* Button */}
          <div>
            {testStatus === 'idle' || testStatus === 'failed' ? (
              <button data-testid="test-call-btn" onClick={handleTestCall}
                disabled={!testPhone.trim() || !testCampaignId}
                style={{ padding: '0.55rem 1.4rem', borderRadius: '6px', fontWeight: 600, whiteSpace: 'nowrap',
                  background: (!testPhone.trim() || !testCampaignId) ? 'hsl(var(--muted))' : 'hsl(var(--baby-blue))',
                  color: (!testPhone.trim() || !testCampaignId) ? 'hsl(var(--muted-foreground))' : '#fff',
                  border: 'none', cursor: (!testPhone.trim() || !testCampaignId) ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem' }}>
                📞 Call Now
              </button>
            ) : (
              <button data-testid="test-call-reset-btn" onClick={resetTest}
                style={{ padding: '0.55rem 1.2rem', borderRadius: '6px', fontWeight: 600,
                  background: 'hsl(var(--muted))', color: 'hsl(var(--foreground))',
                  border: 'none', cursor: 'pointer', fontSize: '0.95rem' }}>
                Reset
              </button>
            )}
          </div>
        </div>

        {/* Status bar */}
        {testStatus !== 'idle' && (
          <div data-testid="test-call-status" style={{ marginTop: '1rem', padding: '0.75rem 1rem', borderRadius: '8px',
            background: testStatus === 'connected' ? 'rgba(34,197,94,0.1)' : testStatus === 'failed' ? 'rgba(239,68,68,0.1)' : 'rgba(245,158,11,0.1)',
            border: `1px solid ${statusColor[testStatus]}30`,
            display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700, color: statusColor[testStatus], whiteSpace: 'nowrap' }}>
              {statusLabel[testStatus]}
            </div>
            {testResult && (
              <div style={{ fontSize: '0.85rem', color: 'hsl(var(--muted-foreground))', lineHeight: 1.6 }}>
                <span style={{ marginRight: '1.2rem' }}>📱 <b>{testResult.phone_number}</b></span>
                <span style={{ marginRight: '1.2rem' }}>📋 {testResult.campaign_name}</span>
                {testResult.caller_id && <span style={{ marginRight: '1.2rem' }}>🪪 ID: {testResult.caller_id}</span>}
                {testResult.audio_file && <span>🔊 {testResult.audio_file}</span>}
              </div>
            )}
            {testError && <div style={{ fontSize: '0.85rem', color: '#ef4444' }}>{testError}</div>}
          </div>
        )}
      </div>

      {/* Recent Transfers */}
      <div className="card" style={{ marginTop: '1.5rem' }}>
        <div className="card-header">
          <h3 className="card-title">Recent Transfers</h3>
          <p className="card-description">Prospects who pressed 1</p>
        </div>
        <div className="table-container">
          <table className="data-table" data-testid="recent-transfers-table">
            <thead>
              <tr><th>Name</th><th>Phone</th><th>Address</th><th>City</th><th>State</th><th>Campaign</th><th>Time</th></tr>
            </thead>
            <tbody>
              {recentTransfers.length === 0 ? (
                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '1rem' }}>No transfers yet</td></tr>
              ) : recentTransfers.map(t => (
                <tr key={t.id} data-testid="transfer-row">
                  <td style={{ fontWeight: 600 }}>{t.first_name || t.last_name ? `${t.first_name || ''} ${t.last_name || ''}`.trim() : '-'}</td>
                  <td>{t.phone_number}</td>
                  <td>{t.address || '-'}</td>
                  <td>{t.city || '-'}</td>
                  <td>{t.state || '-'}</td>
                  <td>{t.campaign_name || '-'}</td>
                  <td style={{ fontSize: '0.85rem' }}>{t.call_time ? new Date(t.call_time).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recent Campaigns */}
      <div className="card">
        <div className="card-header"><h3 className="card-title">Recent Campaigns</h3></div>
        {campaigns.length === 0 ? (
          <p style={{ color: 'hsl(var(--muted-foreground))' }}>No campaigns yet.</p>
        ) : (
          <div className="table-container">
            <table>
              <thead><tr><th>Campaign</th><th>Status</th><th>Leads</th><th>Dialed</th><th>Transferred</th></tr></thead>
              <tbody>
                {campaigns.map(c => (
                  <tr key={c.id}>
                    <td>{c.name}</td>
                    <td><span className={`badge badge-${c.status}`}>{c.status}</span></td>
                    <td>{c.stats.total_leads}</td>
                    <td>{c.stats.dialed}</td>
                    <td>{c.stats.transferred}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dashboard;
